import React, { useState, useRef } from 'react';
import {
  Container, Paper, Typography, Box, TextField, Button, Grid, Tab, Tabs,
  Card, CardMedia, CardContent, CardActions, IconButton, Snackbar, Alert,
  CircularProgress, Divider, Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ImageIcon from '@mui/icons-material/Image';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { searchPatternByCode, searchPatternByImage } from '../services/patternService';

// 自定义样式
const Input = styled('input')({
  display: 'none',
});

const ImagePreview = styled(Box)(({ theme }) => ({
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  height: '250px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  backgroundColor: theme.palette.background.default,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const PatternSearch = () => {
  // 状态管理
  const [tabValue, setTabValue] = useState(0);
  const [codeSearch, setCodeSearch] = useState('');
  const [imageData, setImageData] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);
  
  // 处理标签切换
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // 重置搜索结果
    setSearchResults([]);
  };

  // 处理代码输入变更
  const handleCodeInputChange = (e) => {
    setCodeSearch(e.target.value);
  };

  // 处理文件选择
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImageData(reader.result);
          setPreviewImage(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        showSnackbar('请选择有效的图片文件', 'error');
      }
    }
  };

  // 处理粘贴事件
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const blob = items[i].getAsFile();
        const reader = new FileReader();
        reader.onloadend = () => {
          setImageData(reader.result);
          setPreviewImage(reader.result);
        };
        reader.readAsDataURL(blob);
        break;
      }
    }
  };

  // 处理图片区域点击
  const handleImageAreaClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // 清除图片
  const handleClearImage = () => {
    setImageData(null);
    setPreviewImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 显示提示信息
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // 关闭提示
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 搜索
  const handleSearch = async () => {
    try {
      setLoading(true);
      setSearchResults([]);
      
      if (tabValue === 0) {
        // 代码搜索
        if (!codeSearch.trim()) {
          showSnackbar('请输入搜索代码', 'error');
          return;
        }
        
        const response = await searchPatternByCode(codeSearch.trim());
        if (response.success) {
          setSearchResults(response.data);
          if (response.data.length === 0) {
            showSnackbar('未找到匹配的样板', 'info');
          }
        } else {
          showSnackbar(response.message || '搜索失败', 'error');
        }
      } else {
        // 图像搜索
        if (!imageData) {
          showSnackbar('请上传需要搜索的图片', 'error');
          return;
        }
        
        const response = await searchPatternByImage(imageData);
        if (response.success) {
          setSearchResults(response.data);
          if (response.data.length === 0) {
            showSnackbar('未找到匹配的样板', 'info');
          }
        } else {
          showSnackbar(response.message || '搜索失败', 'error');
        }
      }
    } catch (error) {
      console.error('搜索错误', error);
      showSnackbar('搜索失败: ' + (error.message || '未知错误'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // 复制代码到剪贴板
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code)
      .then(() => {
        showSnackbar('代码已复制到剪贴板');
      })
      .catch((error) => {
        console.error('复制失败:', error);
        showSnackbar('复制失败', 'error');
      });
  };

  // 渲染搜索结果卡片
  const renderResultCard = (pattern) => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardMedia
        component="div"
        sx={{
          pt: '75%',
          position: 'relative',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundColor: '#f5f5f5'
        }}
        image={pattern.image_url || '/placeholder-image.jpg'}
      >
        {tabValue === 1 && pattern.similarity !== undefined && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              p: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              borderBottomRightRadius: '8px'
            }}
          >
            <Chip 
              label={`相似度: ${(pattern.similarity * 100).toFixed(1)}%`} 
              size="small" 
              color={pattern.similarity > 0.7 ? 'success' : 'primary'} 
              variant="outlined"
            />
          </Box>
        )}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            p: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            borderBottomLeftRadius: '8px'
          }}
        >
          <Chip 
            label={pattern.category_name || '未分类'} 
            size="small" 
            color="primary" 
            variant="outlined"
          />
        </Box>
      </CardMedia>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="h6" component="h2" noWrap>
          {pattern.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ flexGrow: 1 }}>
            {pattern.code}
          </Typography>
          <IconButton 
            size="small" 
            color="primary" 
            onClick={() => handleCopyCode(pattern.code)}
            aria-label="复制代码"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {pattern.description?.length > 50 
            ? `${pattern.description.substring(0, 50)}...` 
            : pattern.description || '无描述'}
        </Typography>
      </CardContent>
      <CardActions>
        <IconButton 
          size="small" 
          color="primary" 
          onClick={() => window.open(`/pattern/${pattern.id}`, '_blank')}
          aria-label="查看详情"
        >
          <VisibilityIcon />
        </IconButton>
      </CardActions>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        <Typography component="h2" variant="h6" color="primary" gutterBottom>
          样板搜索
        </Typography>
        
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{ mb: 3 }}
        >
          <Tab label="编码搜索" />
          <Tab label="图像搜索" />
        </Tabs>
        
        {tabValue === 0 ? (
          // 代码搜索
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="输入样板编码"
              variant="outlined"
              value={codeSearch}
              onChange={handleCodeInputChange}
              placeholder="例如: SP1-20250327-1234"
              InputProps={{
                endAdornment: (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    disabled={loading}
                    sx={{ ml: 1 }}
                  >
                    {loading ? <CircularProgress size={24} /> : '搜索'}
                  </Button>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
          </Box>
        ) : (
          // 图像搜索
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ImagePreview 
                  onClick={handleImageAreaClick}
                  onPaste={handlePaste}
                  tabIndex={0}
                  sx={{ outline: 'none' }}
                >
                  {previewImage ? (
                    <>
                      <img 
                        src={previewImage} 
                        alt="预览" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '100%', 
                          objectFit: 'contain' 
                        }} 
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearImage();
                        }}
                        sx={{ 
                          position: 'absolute', 
                          top: 8, 
                          right: 8,
                          backgroundColor: 'rgba(255,255,255,0.7)'
                        }}
                      >
                        清除
                      </Button>
                    </>
                  ) : (
                    <>
                      <ImageIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" align="center">
                        点击上传或拖拽图片到此处
                      </Typography>
                      <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 1 }}>
                        支持直接粘贴图片 (Ctrl+V)
                      </Typography>
                    </>
                  )}
                </ImagePreview>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <input
                    accept="image/*"
                    type="file"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                  />
                  <Button 
                    variant="contained" 
                    component="span"
                    startIcon={<FileUploadIcon />}
                    onClick={handleImageAreaClick}
                    sx={{ mr: 1 }}
                  >
                    上传图片
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    disabled={loading || !imageData}
                  >
                    {loading ? <CircularProgress size={24} /> : '搜索'}
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper elevation={0} variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>图像搜索说明</Typography>
                  <Typography variant="body2" paragraph>
                    上传一张样板图片，系统将自动检索相似的样板。
                  </Typography>
                  <Typography variant="body2" paragraph>
                    支持的图片格式: JPG, PNG, GIF
                  </Typography>
                  <Typography variant="body2" paragraph>
                    搜索结果将按相似度排序，展示最匹配的样板。
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    提示: 您可以直接将图片拖放到左侧区域，或使用剪贴板粘贴图片(Ctrl+V)。
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
        
        {/* 搜索结果 */}
        {searchResults.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              搜索结果 ({searchResults.length})
            </Typography>
            <Grid container spacing={3}>
              {searchResults.map((pattern) => (
                <Grid item key={pattern.id} xs={12} sm={6} md={3}>
                  {renderResultCard(pattern)}
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Paper>

      {/* 提示消息 */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PatternSearch;
